/**
 * Pestaña Análisis del repositorio de Redes Sociales: comparativa de performance por red,
 * cliente o perfil (con índice compuesto), matriz alcance × tasa de interacción, distribución
 * estadística por red, top posts y atípicos, mejor día y hora, rendimiento por tipo de post,
 * correlación entre métricas y nube de palabras / hashtags. Usa los posts del periodo y el
 * cierre mensual de seguidores.
 */
import React, { useMemo, useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart,
    Tooltip, XAxis, YAxis, ZAxis,
} from 'recharts';
import { ink, studio, FilterPill } from '../admin/studioUi';
import { downloadCsv } from './repoApi';
import NetworkDot from './NetworkDot';
import InfoTip from './InfoTip';
import { rt, useFitColumns, HiddenColumnsNote } from './repoTable';
import LoadingBlock from './LoadingBlock';
import usePeriodPosts from './usePeriodPosts';
import { PostDetail, Thumb } from './SocialPosts';
import MobileCards from './MobileCards';
import {
    AnatomySection, BenchmarkSection, FrequencySection, InsightsPanel, MomentumSection, ParetoSection,
} from './AnalysisAdvanced';
import {
    buildInsights, frequencyAnalysis, momentum, networkBenchmark, paretoCurve, postAnatomy,
} from './analysisExtras';
import { evaluateKpi, formatKpi } from './customKpis';
import {
    NETWORK_COLORS, engagementRate, formatCompact, formatMetric, formatNumber, formatPct, formatSigned, inPeriod, isBlank,
    matchesSelection, metricsWithData, num, previousPeriod, profileKey, sumRows,
} from './socialRepoUtils';
import {
    WEEKDAYS, comparePerformance, correlationMatrix, describe, postingSchedule, quadrantOf, quantile,
    weekdayHourHeatmap, wordFrequencies,
} from './analysisUtils';

const GROUP_OPTIONS = [
    { id: 'red', label: 'Por red social' },
    { id: 'client', label: 'Por cliente' },
    { id: 'profile', label: 'Por perfil' },
];
const FOCUS_METRICS = ['Interacciones', 'Alcance', 'Impresiones', 'Vistas', 'Likes', 'Comentarios', 'Compartidos', 'Guardados', 'Clics'];
const CORRELATION_METRICS = ['Alcance', 'Impresiones', 'Vistas', 'Likes', 'Reacciones', 'Comentarios', 'Compartidos', 'Guardados', 'Clics', 'Interacciones'];
const QUADRANT_COLORS = { Estrella: '#137333', Nicho: '#1a73e8', Masivo: '#b06000', Rezagado: '#80868b' };
const QUADRANT_HINTS = {
    Estrella: 'mucho alcance y alta interacción',
    Nicho: 'poco alcance pero alta interacción',
    Masivo: 'mucho alcance, baja interacción',
    Rezagado: 'poco alcance y baja interacción',
};
const CLOUD_COLORS = ['#5E35B1', '#1E88E5', '#D81B60', '#00897B', '#F4511E', '#3949AB', '#8E24AA', '#6D4C41'];
const MIN_HEAT_POSTS = 3;
const EMPTY_MAP = new Map();

function Section({ title, hint, actions, children, info }) {
    return (
        <section style={studio.card}>
            <div style={styles.cardHead}>
                <div style={{ minWidth: 0 }}>
                    <h3 style={styles.cardTitle}>{title}{info && <InfoTip term={info} />}</h3>
                    {hint && <p style={studio.hint}>{hint}</p>}
                </div>
                {actions}
            </div>
            {children}
        </section>
    );
}

/** Caja y bigotes horizontal en SVG (escala lineal hasta `max`). */
function BoxPlot({ stats, max, color }) {
    if (!stats || !max) return null;
    const x = v => Math.max(0, Math.min(100, (v / max) * 100));
    const whiskerHigh = Math.min(stats.max, stats.upperFence);
    return (
        <svg viewBox="0 0 100 16" preserveAspectRatio="none" style={{ width: '100%', height: 18, display: 'block' }}>
            <line x1={x(stats.min)} x2={x(whiskerHigh)} y1="8" y2="8" stroke={ink.lineStrong} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
            <rect x={x(stats.q1)} y="3" width={Math.max(0.5, x(stats.q3) - x(stats.q1))} height="10" fill={color} opacity="0.25" stroke={color} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1={x(stats.median)} x2={x(stats.median)} y1="2" y2="14" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

function heatColor(ratio) {
    if (ratio == null) return ink.wash;
    const alpha = 0.08 + 0.82 * ratio;
    return `rgba(94, 53, 177, ${alpha.toFixed(3)})`;
}

function corrColor(value) {
    if (value == null) return ink.wash;
    const alpha = Math.min(1, Math.abs(value)) * 0.85;
    return value >= 0 ? `rgba(19, 115, 51, ${alpha.toFixed(3)})` : `rgba(197, 34, 31, ${alpha.toFixed(3)})`;
}

const SUBVIEWS = [
    { id: 'comparativa', label: 'Comparativa' },
    { id: 'contenido', label: 'Contenido' },
    { id: 'tiempos', label: 'Tiempos' },
    { id: 'tendencias', label: 'Tendencias' },
];

/** Por perfil: posts y tasa en un conjunto de filas mensuales. */
function profileRates(rows) {
    const map = new Map();
    rows.forEach((row) => {
        const key = profileKey(row);
        if (!map.has(key)) map.set(key, { key, Red: row.Red, Perfil: row.Perfil, rows: [] });
        map.get(key).rows.push(row);
    });
    const out = new Map();
    map.forEach((item, key) => {
        out.set(key, { key, Red: item.Red, Perfil: item.Perfil, posts: sumRows(item.rows, ['Publicaciones']).Publicaciones, rate: engagementRate(item.rows) });
    });
    return out;
}

const SocialAnalysis = ({ repo, kpis = [], clientOf = EMPTY_MAP, onToggleExclude, followersMonthly, monthlyMerged = [], selection, period, dataVersion, narrow }) => {
    const [scheduleMeasure, setScheduleMeasure] = useState('count');
    const [subview, setSubview] = useState('comparativa');
    const [groupBy, setGroupBy] = useState('red');
    const [focus, setFocus] = useState('Interacciones');
    const [sort, setSort] = useState({ key: 'score', dir: 'desc' });
    const [cloudMode, setCloudMode] = useState('words');
    const [cloudWeight, setCloudWeight] = useState('count');
    const [selectedWord, setSelectedWord] = useState(null);
    const [detail, setDetail] = useState(null);

    const postsState = usePeriodPosts(repo, selection, period, { dataVersion });
    const posts = useMemo(() => postsState.rows.filter(row => matchesSelection(row, selection)), [postsState.rows, selection]);
    const postKpis = useMemo(() => kpis.filter(kpi => kpi.groups.length === 1 && kpi.groups[0] === 'posts'), [kpis]);
    const isKpiFocus = focus.startsWith('kpi:');
    const focusKpi = isKpiFocus ? postKpis.find(k => `kpi:${k.id}` === focus) : null;
    const focusValue = useMemo(
        () => (focusKpi ? (row => evaluateKpi(focusKpi, row) ?? 0) : (row => num(row[focus]))),
        [focus, focusKpi]
    );
    const formatFocus = (value) => (focusKpi ? formatKpi(focusKpi, value) : formatMetric(focus, value));

    /* Seguidores del periodo por perfil: cierre del mes anterior al periodo y del último mes. */
    const followerRows = useMemo(() => {
        const scoped = followersMonthly.filter(row => matchesSelection(row, selection));
        const map = new Map();
        scoped.forEach((row) => {
            const key = profileKey(row);
            if (!map.has(key)) map.set(key, { Red: row.Red, Perfil: row.Perfil, Nuevos: 0, Perdidos: 0, Neto: 0, startValue: 0, startMonth: '', endValue: 0, endMonth: '' });
            const item = map.get(key);
            const close = num(row['Seguidores al Cierre']);
            if (row.Mes < period.from && row.Mes > item.startMonth && close > 0) { item.startValue = close; item.startMonth = row.Mes; }
            if (row.Mes >= period.from && row.Mes <= period.to) {
                item.Nuevos += num(row.Nuevos);
                item.Perdidos += num(row.Perdidos);
                item.Neto += num(row.Neto);
                if (row.Mes > item.endMonth && close > 0) { item.endValue = close; item.endMonth = row.Mes; }
            }
        });
        return Array.from(map.values()).filter(item => inPeriod([{ Mes: item.endMonth }], period).length || item.Neto);
    }, [followersMonthly, selection, period]);

    const groupOf = useMemo(() => {
        if (groupBy === 'client') return row => clientOf.get(profileKey(row)) || row.Perfil;
        if (groupBy === 'profile') return row => `${row.Perfil} · ${row.Red}`;
        return row => row.Red;
    }, [groupBy, clientOf]);

    /* Perfiles de cada grupo, para poder omitir el grupo completo. */
    const groupKeys = useMemo(() => {
        const map = new Map();
        [...posts, ...followerRows].forEach((row) => {
            const name = groupOf(row);
            if (!map.has(name)) map.set(name, new Set());
            map.get(name).add(profileKey(row));
        });
        return map;
    }, [posts, followerRows, groupOf]);

    const comparison = useMemo(() => {
        const list = comparePerformance(posts, followerRows, groupOf).map(item => ({
            ...item,
            ...Object.fromEntries(postKpis.map(kpi => [`kpi:${kpi.id}`, evaluateKpi(kpi, item.rows)])),
        }));
        const factor = sort.dir === 'asc' ? 1 : -1;
        return list.sort((a, b) => {
            const va = a[sort.key];
            const vb = b[sort.key];
            if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb), 'es') * factor;
            return ((va ?? -Infinity) - (vb ?? -Infinity)) * factor;
        });
    }, [posts, followerRows, groupOf, postKpis, sort]);

    /* Matriz de posicionamiento por perfil. */
    const matrix = useMemo(() => {
        const perProfile = comparePerformance(posts, [], row => `${row.Perfil} · ${row.Red}`)
            .filter(item => item.posts >= 2 && item.reachPerPost > 0 && item.rate != null);
        const xs = perProfile.map(i => i.reachPerPost).sort((a, b) => a - b);
        const ys = perProfile.map(i => i.rate).sort((a, b) => a - b);
        const mx = quantile(xs, 0.5);
        const my = quantile(ys, 0.5);
        const points = perProfile.map((item) => {
            const red = item.rows[0]?.Red;
            return {
                name: item.name,
                key: item.rows[0] ? profileKey(item.rows[0]) : '',
                red,
                x: item.reachPerPost,
                y: item.rate,
                z: item.posts,
                quadrant: quadrantOf(item.reachPerPost, item.rate, mx, my),
            };
        });
        const byQuadrant = {};
        points.forEach((p) => { (byQuadrant[p.quadrant] = byQuadrant[p.quadrant] || []).push(p.name); });
        return { points, mx, my, byQuadrant };
    }, [posts]);

    /* Distribución de la métrica de referencia por red. */
    const distribution = useMemo(() => {
        const byRed = new Map();
        posts.forEach((row) => {
            if (!byRed.has(row.Red)) byRed.set(row.Red, []);
            byRed.get(row.Red).push(focusValue(row));
        });
        const list = Array.from(byRed.entries())
            .map(([red, values]) => ({ red, stats: describe(values) }))
            .filter(item => item.stats)
            .sort((a, b) => b.stats.median - a.stats.median);
        const max = Math.max(0, ...list.map(item => Math.min(item.stats.max, item.stats.upperFence)));
        return { list, max };
    }, [posts, focusValue]);

    const topPosts = useMemo(() => {
        const fences = new Map(distribution.list.map(item => [item.red, item.stats.upperFence]));
        return [...posts]
            .map(row => ({ row, value: focusValue(row), outlier: focusValue(row) > (fences.get(row.Red) ?? Infinity) }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [posts, focusValue, distribution.list]);
    const outlierCount = distribution.list.reduce((acc, item) => acc + item.stats.outliers, 0);

    const heat = useMemo(() => {
        const scored = posts.map(row => ({ ...row, __focus: focusValue(row) }));
        const { cells, slots } = weekdayHourHeatmap(scored, '__focus');
        let best = null;
        let maxMedian = 0;
        cells.forEach((day, d) => day.forEach((cell, s) => {
            if (cell.count >= MIN_HEAT_POSTS && cell.median != null) {
                maxMedian = Math.max(maxMedian, cell.median);
                if (!best || cell.median > best.median) best = { day: d, slot: s, ...cell };
            }
        }));
        return { cells, slots, best, maxMedian };
    }, [posts, focusValue]);

    const schedule = useMemo(
        () => postingSchedule(posts, { measure: scheduleMeasure, scoreOf: focusValue }),
        [posts, scheduleMeasure, focusValue]
    );

    const byType = useMemo(() => {
        const map = new Map();
        posts.forEach((row) => {
            const type = isBlank(row.Tipo) ? 'Sin tipo' : row.Tipo;
            const key = `${row.Red}|${type}`;
            if (!map.has(key)) map.set(key, { red: row.Red, type, values: [], rows: [] });
            map.get(key).values.push(focusValue(row));
            map.get(key).rows.push(row);
        });
        return Array.from(map.values())
            .map(item => ({ ...item, stats: describe(item.values) }))
            .sort((a, b) => (b.stats?.median || 0) - (a.stats?.median || 0));
    }, [posts, focusValue]);

    const correlation = useMemo(() => {
        const metrics = metricsWithData(posts, CORRELATION_METRICS);
        return { metrics, matrix: correlationMatrix(posts, metrics) };
    }, [posts]);

    const words = useMemo(() => wordFrequencies(posts, { mode: cloudMode, limit: 70 }), [posts, cloudMode]);
    const cloud = useMemo(() => {
        const weightOf = w => (cloudWeight === 'count' ? w.count : w.avgInteractions);
        const eligible = cloudWeight === 'count' ? words : words.filter(w => w.posts >= 2);
        const max = Math.max(1, ...eligible.map(weightOf));
        /* Orden estable pero mezclado para que la nube no quede de mayor a menor. */
        return eligible
            .map((w, i) => ({ ...w, size: 0.8 + 1.9 * Math.sqrt(weightOf(w) / max), color: CLOUD_COLORS[i % CLOUD_COLORS.length], order: (i * 37) % eligible.length }))
            .sort((a, b) => a.order - b.order);
    }, [words, cloudWeight]);

    const metricLabel = focusKpi ? focusKpi.name : focus.toLowerCase();
    const anatomy = useMemo(() => postAnatomy(posts, focusValue), [posts, focusValue]);
    const pareto = useMemo(() => {
        const byRed = new Map();
        posts.forEach((row) => {
            if (!byRed.has(row.Red)) byRed.set(row.Red, []);
            byRed.get(row.Red).push(focusValue(row));
        });
        return {
            overall: paretoCurve(posts.map(focusValue)),
            byRed: Array.from(byRed.entries())
                .map(([red, values]) => ({ red, ...paretoCurve(values) }))
                .filter(item => item.top20 != null),
        };
    }, [posts, focusValue]);
    const frequency = useMemo(() => frequencyAnalysis(posts, period, focusValue), [posts, period, focusValue]);
    const merged = useMemo(() => monthlyMerged.filter(row => matchesSelection(row, selection)), [monthlyMerged, selection]);
    const momentumRows = useMemo(() => momentum(merged, period.to, {
        window: 6,
        metric: focusKpi ? (rows => evaluateKpi(focusKpi, rows) ?? 0) : focus,
    }), [merged, period.to, focus, focusKpi]);
    const benchmark = useMemo(() => networkBenchmark(posts), [posts]);
    const insights = useMemo(() => buildInsights({
        current: profileRates(inPeriod(merged, period)),
        previous: profileRates(inPeriod(merged, previousPeriod(period))),
        anatomy,
        pareto: pareto.overall,
        heatBest: heat.best ? { label: `${WEEKDAYS[heat.best.day]} ${heat.slots[heat.best.slot]} h`, median: heat.best.median, count: heat.best.count } : null,
        frequency,
        momentumRows,
        benchmark,
        metricLabel,
        formatValue: formatFocus,
    }), [merged, period, anatomy, pareto.overall, heat, frequency, momentumRows, benchmark, metricLabel]); // eslint-disable-line react-hooks/exhaustive-deps -- formatFocus deriva de focus

    const groupLabel = GROUP_OPTIONS.find(g => g.id === groupBy)?.label.replace('Por ', '') || '';
    const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
    const columns = [
        { key: 'name', min: 150, label: groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1), info: groupBy === 'client' ? 'cliente' : null },
        { key: 'profiles', min: 62, priority: 9, label: 'Perfiles', fmt: formatNumber },
        { key: 'posts', min: 54, priority: 2, label: 'Posts', fmt: formatNumber, info: 'Publicaciones' },
        { key: 'Alcance', min: 70, priority: 6, label: 'Alcance', fmt: formatCompact, info: 'Alcance' },
        { key: 'Interacciones', min: 72, priority: 3, label: 'Interacc.', fmt: formatCompact, info: 'Interacciones' },
        { key: 'perPost', min: 70, priority: 4, label: 'Int./post', fmt: v => (v == null ? '—' : formatNumber(v)), info: 'perPost' },
        { key: 'median', min: 72, priority: 5, label: 'Mediana int.', fmt: v => (v == null ? '—' : formatNumber(v)), info: 'mediana' },
        { key: 'cv', min: 70, priority: 8, label: 'Variación', fmt: v => (v == null ? '—' : formatPct(v, 0)), info: 'variacion' },
        { key: 'rate', min: 68, priority: 1, label: 'Tasa int.', fmt: v => formatPct(v, 2), info: 'tasa' },
        { key: 'share', min: 66, priority: 5, label: 'Particip.', fmt: v => formatPct(v, 1), info: 'participacion' },
        { key: 'Neto', min: 72, priority: 7, label: 'Seg. netos', fmt: formatSigned, info: 'neto' },
        { key: 'growth', min: 70, priority: 6, label: 'Crec. seg.', fmt: v => formatPct(v, 2), info: 'crecimiento' },
        { key: 'score', min: 112, priority: 1, label: 'Índice', fmt: v => (v == null ? '—' : v.toFixed(2)), info: 'indice' },
        ...postKpis.map(kpi => ({ key: `kpi:${kpi.id}`, min: 80, priority: 4, label: kpi.name, fmt: v => formatKpi(kpi, v), kpi, info: 'kpiPropio' })),
    ];
    const [compareRef, compareColumns, compareHidden] = useFitColumns(columns, { reserve: onToggleExclude ? 64 : 0 });
    const distColumns = [
        { key: 'red', label: 'Red', min: 96 },
        { key: 'n', label: 'Posts', min: 50, priority: 3 },
        { key: 'min', label: 'Mín.', min: 58, priority: 7 },
        { key: 'q1', label: 'Q1', min: 58, priority: 2, info: { what: 'Primer cuartil: el 25% de los posts quedó por debajo de este valor.', how: 'Percentil 25 con interpolación lineal.' } },
        { key: 'median', label: 'Mediana', min: 64, priority: 1, info: 'mediana' },
        { key: 'q3', label: 'Q3', min: 58, priority: 2, info: { what: 'Tercer cuartil: el 75% de los posts quedó por debajo de este valor.', how: 'Percentil 75 con interpolación lineal.' } },
        { key: 'max', label: 'Máx.', min: 64, priority: 5 },
        { key: 'mean', label: 'Media', min: 64, priority: 4 },
        { key: 'sd', label: 'Desv. est.', min: 64, priority: 6, info: 'desviacion' },
        { key: 'outliers', label: 'Atípicos', min: 58, priority: 3, info: 'atipicos' },
        { key: 'box', label: 'Distribución', min: 160, priority: 2 },
    ];
    const [distRef, distVisible, distHidden] = useFitColumns(distColumns);
    const maxScore = Math.max(0.01, ...comparison.map(i => Math.abs(i.score || 0)));

    const exportComparison = () => downloadCsv(`redes-sociales-comparativa-${groupBy}-${period.from}_${period.to}.csv`,
        columns.map(col => ({ label: col.label, value: r => (r[col.key] == null ? '' : r[col.key]) })), comparison);

    if (postsState.loading && !postsState.rows.length) return <LoadingBlock rows={3} label="Analizando las publicaciones del periodo…" />;

    return (
        <div style={styles.wrap}>
            <div style={styles.toolbar}>
                <FilterPill value={groupBy} onChange={setGroupBy} style={{ minWidth: 170 }}>
                    {GROUP_OPTIONS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                </FilterPill>
                <FilterPill value={focus} onChange={setFocus} style={{ minWidth: 200 }}>
                    {FOCUS_METRICS.map(m => <option key={m} value={m}>Métrica: {m}</option>)}
                    {postKpis.length > 0 && (
                        <optgroup label="Tus KPIs">
                            {postKpis.map(kpi => <option key={kpi.id} value={`kpi:${kpi.id}`}>Métrica: {kpi.name}</option>)}
                        </optgroup>
                    )}
                </FilterPill>
                <span style={studio.hint}>
                    {formatNumber(posts.length)} publicaciones analizadas
                    {postsState.truncated && ' (las 5,000 más recientes)'}
                    {postsState.loading && ' · actualizando…'}
                </span>
            </div>
            {postsState.error && <div style={styles.error}>{postsState.error}</div>}
            {groupBy === 'client' && (
                <p style={{ ...studio.hint, margin: '-6px 0 0' }}>
                    El cliente se deduce del nombre del perfil (une la misma marca en todas sus redes y usa el nombre del
                    cliente del sistema cuando coincide).
                </p>
            )}

            <InsightsPanel insights={insights} onGo={setSubview} />

            <div style={narrow ? styles.subnavScroll : undefined}>
                <div style={styles.subnav} role="tablist" aria-label="Secciones del análisis">
                    {SUBVIEWS.map(v => (
                        <button
                            key={v.id}
                            type="button"
                            role="tab"
                            aria-selected={subview === v.id}
                            onClick={() => setSubview(v.id)}
                            style={{ ...styles.subnavBtn, ...(subview === v.id ? styles.subnavBtnOn : {}) }}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
            </div>

            {subview === 'comparativa' && (
                <>
            <Section
                title={`Comparativa de performance · ${groupLabel}`}
                info="indice"
                hint="Clic en un encabezado para ordenar; «Omitir» quita el grupo de todos los cálculos. El índice resume tasa, interacciones por post y crecimiento (0 = promedio)."
                actions={<button type="button" onClick={exportComparison} style={studio.ghostBtn} disabled={!comparison.length}>Exportar CSV</button>}
            >
                {narrow ? (
                    <MobileCards
                        items={comparison}
                        keyOf={item => item.name}
                        titleOf={item => (groupBy === 'red' ? <><NetworkDot red={item.name} />{item.name}</> : item.name)}
                        subtitleOf={item => `${formatNumber(item.profiles)} perfiles · ${formatNumber(item.posts)} posts · índice ${item.score == null ? '—' : item.score.toFixed(2)}`}
                        metricsOf={item => [
                            { label: 'Interacc.', value: formatCompact(item.Interacciones) },
                            { label: 'Int./post', value: item.perPost == null ? '—' : formatNumber(item.perPost) },
                            { label: 'Tasa int.', value: formatPct(item.rate, 2) },
                            { label: 'Alcance', value: formatCompact(item.Alcance) },
                            { label: 'Particip.', value: formatPct(item.share, 1) },
                            { label: 'Crec. seg.', value: formatPct(item.growth, 2) },
                            ...postKpis.map(kpi => ({ label: kpi.name, value: formatKpi(kpi, item[`kpi:${kpi.id}`]), color: ink.accentText })),
                        ]}
                        actionOf={onToggleExclude ? (item => (
                            <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude(Array.from(groupKeys.get(item.name) || []))}>Omitir</button>
                        )) : undefined}
                        empty="Sin publicaciones en el periodo."
                    />
                ) : (
                <div ref={compareRef}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {compareColumns.map(col => (
                                    <th
                                        key={col.key}
                                        onClick={() => toggleSort(col.key)}
                                        style={{ ...rt.th, cursor: 'pointer', textAlign: col.key === 'name' ? 'left' : 'right', ...(col.kpi ? { color: ink.accentText } : {}) }}
                                    >
                                        {col.label}{sort.key === col.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        {col.info && <InfoTip term={col.info} how={col.kpi ? `Fórmula: ${col.kpi.formula}. Se suma cada campo de los posts del grupo y luego se aplica la fórmula.` : undefined} />}
                                    </th>
                                ))}
                                {onToggleExclude && <th style={rt.th} />}
                            </tr>
                        </thead>
                        <tbody>
                            {comparison.map(item => (
                                <tr key={item.name}>
                                    <td style={{ ...rt.td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {groupBy === 'red'
                                            ? <span style={styles.redCell}><NetworkDot red={item.name} />{item.name}</span>
                                            : item.name}
                                    </td>
                                    {compareColumns.slice(1).map(col => (
                                        <td key={col.key} style={{ ...styles.numTd, ...(col.kpi ? { color: ink.accentText } : {}) }}>
                                            {col.key === 'score' ? (
                                                <span style={styles.scoreCell}>
                                                    <span style={styles.scoreTrack}>
                                                        <span style={{
                                                            ...styles.scoreBar,
                                                            width: `${(Math.abs(item.score || 0) / maxScore) * 50}%`,
                                                            left: (item.score || 0) >= 0 ? '50%' : `${50 - (Math.abs(item.score || 0) / maxScore) * 50}%`,
                                                            backgroundColor: (item.score || 0) >= 0 ? '#34A853' : '#EA4335',
                                                        }} />
                                                    </span>
                                                    {col.fmt(item.score)}
                                                </span>
                                            ) : col.fmt(item[col.key])}
                                        </td>
                                    ))}
                                    {onToggleExclude && (
                                        <td style={{ ...rt.td, textAlign: 'right' }}>
                                            <button
                                                type="button"
                                                style={styles.omitBtn}
                                                title="Quitar de todos los cálculos y gráficas"
                                                onClick={() => onToggleExclude(Array.from(groupKeys.get(item.name) || []))}
                                            >
                                                Omitir
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {!comparison.length && (
                                <tr><td colSpan={compareColumns.length + 1} style={{ ...rt.td, textAlign: 'center', color: ink.faint }}>Sin publicaciones en el periodo.</td></tr>
                            )}
                        </tbody>
                    </table>
                    <HiddenColumnsNote hidden={compareHidden} />
                </div>
                )}
            </Section>
            <div style={{ ...styles.grid2, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)' }}>
                <Section
                    title="Matriz de posicionamiento de perfiles"
                    info="matriz"
                    hint="Alcance promedio por post contra tasa de interacción; las líneas son las medianas. Tamaño = número de posts. Clic en un punto para omitir el perfil."
                >
                    <div style={{ width: '100%', height: narrow ? 280 : 340 }}>
                        <ResponsiveContainer>
                            <ScatterChart margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                                <CartesianGrid stroke={ink.line} />
                                <XAxis type="number" dataKey="x" name="Alcance por post" tickFormatter={formatCompact} tick={{ fontSize: 11, fill: ink.muted }} scale="log" domain={['auto', 'auto']} allowDataOverflow />
                                <YAxis type="number" dataKey="y" name="Tasa" tickFormatter={v => formatPct(v, 0)} tick={{ fontSize: 11, fill: ink.muted }} width={48} />
                                <ZAxis type="number" dataKey="z" range={[40, 400]} />
                                {matrix.mx != null && <ReferenceLine x={matrix.mx} stroke={ink.faint} strokeDasharray="4 4" />}
                                {matrix.my != null && <ReferenceLine y={matrix.my} stroke={ink.faint} strokeDasharray="4 4" />}
                                <Tooltip
                                    cursor={{ strokeDasharray: '3 3' }}
                                    content={({ payload }) => {
                                        const p = payload?.[0]?.payload;
                                        if (!p) return null;
                                        return (
                                            <div style={styles.tooltip}>
                                                <strong>{p.name}</strong>
                                                <div>Alcance/post: {formatNumber(p.x)}</div>
                                                <div>Tasa: {formatPct(p.y, 2)} · {p.z} posts</div>
                                                <div style={{ color: QUADRANT_COLORS[p.quadrant] }}>{p.quadrant}</div>
                                            </div>
                                        );
                                    }}
                                />
                                {Object.keys(NETWORK_COLORS).map(red => (
                                    <Scatter
                                        key={red}
                                        name={red}
                                        data={matrix.points.filter(p => p.red === red)}
                                        fill={NETWORK_COLORS[red]}
                                        fillOpacity={0.7}
                                        cursor={onToggleExclude ? 'pointer' : undefined}
                                        onClick={(point) => { if (onToggleExclude && point?.key) onToggleExclude([point.key]); }}
                                    />
                                ))}
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                </Section>
                <Section title="Cuadrantes" info="cuadrantes" hint="Según la mediana de los perfiles filtrados">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {['Estrella', 'Nicho', 'Masivo', 'Rezagado'].map(q => (
                            <div key={q} style={{ ...styles.quadrant, borderLeftColor: QUADRANT_COLORS[q] }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                    <strong style={{ color: QUADRANT_COLORS[q] }}>{q}</strong>
                                    <span style={studio.hint}>{(matrix.byQuadrant[q] || []).length} perfiles</span>
                                </div>
                                <div style={studio.hint}>{QUADRANT_HINTS[q]}</div>
                                <div style={styles.quadrantNames}>{(matrix.byQuadrant[q] || []).join(', ') || '—'}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>
                    <BenchmarkSection rows={benchmark} narrow={narrow} onToggleExclude={onToggleExclude} />
                </>
            )}

            {subview === 'contenido' && (
                <>
                    <AnatomySection anatomy={anatomy} formatFocus={formatFocus} metricLabel={metricLabel} narrow={narrow} />
                    <div style={{ ...styles.grid2, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)' }}>
            <Section
                title={`Top 10 posts por ${focusKpi ? focusKpi.name : focus.toLowerCase()}`}
                info="atipicos"
                hint={`${outlierCount} posts atípicos (virales) en el periodo`}
            >
                <div style={styles.topList}>
                    {topPosts.map(({ row, value, outlier }, index) => (
                        <button key={row['Post ID']} type="button" onClick={() => setDetail(row)} style={styles.topItem}>
                            <span style={styles.rank}>{index + 1}</span>
                            <Thumb url={row['Imagen Drive']} size={48} />
                            <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                                <span style={styles.redCell}><NetworkDot red={row.Red} />{row.Perfil}</span>
                                <span style={styles.topText}>{isBlank(row.Texto) ? 'Sin texto' : row.Texto}</span>
                            </span>
                            <span style={styles.topValue}>
                                {formatFocus(value)}
                                {outlier && <span style={styles.viral}>atípico</span>}
                            </span>
                        </button>
                    ))}
                    {!topPosts.length && <p style={studio.hint}>Sin publicaciones.</p>}
                </div>
            </Section>
                        <ParetoSection pareto={pareto.byRed} metricLabel={metricLabel} narrow={narrow} />
                    </div>
            <Section
                title={`Distribución de ${focusKpi ? focusKpi.name : focus.toLowerCase()} por post`}
                info="distribucion"
                hint="Caja = 50% central de los posts (Q1 a Q3), raya = mediana. Atípicos = posts por encima de Q3 + 1.5 × rango intercuartil."
            >
                <div ref={distRef}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {distVisible.map(col => (
                                    <th key={col.key} style={{ ...rt.th, textAlign: col.key === 'red' || col.key === 'box' ? 'left' : 'right', ...(col.key === 'box' ? { width: '26%' } : {}) }}>
                                        {col.label}
                                        {typeof col.info === 'string' && <InfoTip term={col.info} />}
                                        {col.info && typeof col.info === 'object' && <InfoTip what={col.info.what} how={col.info.how} />}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {distribution.list.map(({ red, stats }) => (
                                <tr key={red}>
                                    {distVisible.map((col) => {
                                        if (col.key === 'red') return <td key={col.key} style={rt.td}><span style={styles.redCell}><NetworkDot red={red} />{red}</span></td>;
                                        if (col.key === 'box') return <td key={col.key} style={rt.td}><BoxPlot stats={stats} max={distribution.max} color={NETWORK_COLORS[red] || ink.accent} /></td>;
                                        if (col.key === 'n' || col.key === 'outliers') return <td key={col.key} style={rt.num}>{formatNumber(stats[col.key])}</td>;
                                        return (
                                            <td key={col.key} style={{ ...rt.num, ...(col.key === 'median' ? { fontWeight: 700 } : {}) }}>
                                                {stats[col.key] == null ? '—' : formatFocus(stats[col.key])}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <HiddenColumnsNote hidden={distHidden} />
                </div>
            </Section>
            <div style={{ ...styles.grid2, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)' }}>
                <Section title="Rendimiento por tipo de post" info="porTipo" hint="Mediana y promedio de la métrica por red y tipo">
                    <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                        <table style={rt.table}>
                            <thead>
                                <tr>
                                    {['Red', 'Tipo', 'Posts', 'Mediana', 'Promedio'].map(h => (
                                        <th key={h} style={{ ...rt.th, textAlign: h === 'Red' || h === 'Tipo' ? 'left' : 'right' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {byType.map(item => (
                                    <tr key={`${item.red}|${item.type}`}>
                                        <td style={rt.td}><span style={styles.redCell}><NetworkDot red={item.red} />{item.red}</span></td>
                                        <td style={rt.td}>{item.type}</td>
                                        <td style={styles.numTd}>{formatNumber(item.stats?.n)}</td>
                                        <td style={{ ...styles.numTd, fontWeight: 700 }}>{item.stats ? formatFocus(item.stats.median) : '—'}</td>
                                        <td style={styles.numTd}>{item.stats ? formatFocus(item.stats.mean) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                <Section title="Correlación entre métricas" info="correlacion" hint="Pearson sobre los posts del filtro: verde = suben juntas, rojo = una sube y la otra baja">
                    <div>
                        <table style={{ ...rt.table, tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...rt.th, width: narrow ? 64 : 84 }} />
                                    {correlation.metrics.map(m => (
                                        <th key={m} title={m} style={{ ...rt.th, padding: '6px 1px', textAlign: 'center', fontSize: '0.56rem', overflow: 'hidden', textOverflow: 'clip', whiteSpace: 'nowrap' }}>{m.slice(0, narrow ? 3 : 5)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {correlation.metrics.map((row, i) => (
                                    <tr key={row}>
                                        <td style={{ ...rt.td, padding: '4px 6px', fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row}>{row}</td>
                                        {correlation.matrix[i].map((value, j) => (
                                            <td
                                                key={correlation.metrics[j]}
                                                title={`${row} × ${correlation.metrics[j]}: ${value == null ? 'sin datos' : value.toFixed(2)}`}
                                                style={{
                                                    padding: 0,
                                                    height: 30,
                                                    textAlign: 'center',
                                                    fontSize: '0.7rem',
                                                    backgroundColor: corrColor(value),
                                                    color: value != null && Math.abs(value) > 0.55 ? '#ffffff' : ink.text,
                                                    borderWidth: 1,
                                                    borderStyle: 'solid',
                                                    borderColor: '#ffffff',
                                                }}
                                            >
                                                {value == null || narrow ? '' : value.toFixed(1)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            </div>
            <Section
                title={cloudMode === 'words' ? 'Nube de palabras' : 'Nube de hashtags'}
                info="nube"
                hint={cloudWeight === 'count'
                    ? 'Tamaño = cuántas veces aparece. Clic en una palabra para ver su rendimiento.'
                    : 'Tamaño = interacciones promedio de los posts que la usan (mínimo 2 posts).'}
                actions={(
                    <div style={styles.toolbar}>
                        <FilterPill value={cloudMode} onChange={(v) => { setCloudMode(v); setSelectedWord(null); }} style={{ minWidth: 140 }}>
                            <option value="words">Palabras</option>
                            <option value="hashtags">Hashtags</option>
                        </FilterPill>
                        <FilterPill value={cloudWeight} onChange={setCloudWeight} style={{ minWidth: 190 }}>
                            <option value="count">Tamaño por frecuencia</option>
                            <option value="interactions">Tamaño por interacción</option>
                        </FilterPill>
                    </div>
                )}
            >
                <div style={styles.cloud}>
                    {cloud.map(w => (
                        <button
                            key={w.word}
                            type="button"
                            onClick={() => setSelectedWord(selectedWord?.word === w.word ? null : w)}
                            title={`${w.count} veces · ${w.posts} posts · ${formatNumber(w.avgInteractions)} interacciones promedio`}
                            style={{
                                ...styles.cloudWord,
                                fontSize: `${w.size}rem`,
                                color: w.color,
                                ...(selectedWord?.word === w.word ? { backgroundColor: ink.accentSoft } : {}),
                            }}
                        >
                            {w.word}
                        </button>
                    ))}
                    {!cloud.length && <p style={studio.hint}>No hay suficiente texto en los posts filtrados.</p>}
                </div>
                {selectedWord && (
                    <div style={styles.wordInfo}>
                        <strong>{selectedWord.word}</strong>
                        <span>aparece {formatNumber(selectedWord.count)} veces en {formatNumber(selectedWord.posts)} posts</span>
                        <span>· {formatNumber(selectedWord.avgInteractions)} interacciones promedio por post</span>
                        <span>· {formatPct(posts.length ? selectedWord.posts / posts.length : null, 1)} de las publicaciones</span>
                    </div>
                )}
            </Section>
                </>
            )}

            {subview === 'tiempos' && (
                <>
            <Section
                title="Días y horarios de publicación por red"
                info="horarios"
                hint={scheduleMeasure === 'count'
                    ? 'Cuántos posts publica cada red por día de la semana y por hora del día.'
                    : `Mediana de ${focusKpi ? focusKpi.name : focus.toLowerCase()} de los posts publicados en cada día u hora.`}
                actions={(
                    <FilterPill value={scheduleMeasure} onChange={setScheduleMeasure} style={{ minWidth: 200 }}>
                        <option value="count">Cantidad de posts</option>
                        <option value="median">Rendimiento (mediana)</option>
                    </FilterPill>
                )}
            >
                <div style={{ ...styles.grid2, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 2fr) minmax(0, 3fr)' }}>
                    <div>
                        <p style={styles.chartLabel}>Por día de la semana</p>
                        <div style={{ width: '100%', height: narrow ? 220 : 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={schedule.byDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke={ink.line} vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: ink.muted }} />
                                    <YAxis tick={{ fontSize: 11, fill: ink.muted }} width={44} tickFormatter={scheduleMeasure === 'count' ? (v => v) : formatCompact} allowDecimals={false} />
                                    <Tooltip formatter={(value, name) => [scheduleMeasure === 'count' ? `${formatNumber(value)} posts` : formatFocus(value), name]} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {schedule.networks.map(red => <Bar key={red} dataKey={red} fill={NETWORK_COLORS[red] || ink.accent} maxBarSize={22} />)}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div>
                        <p style={styles.chartLabel}>Por hora del día</p>
                        <div style={{ width: '100%', height: narrow ? 220 : 260 }}>
                            <ResponsiveContainer>
                                <LineChart data={schedule.byHour} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke={ink.line} vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: ink.muted }} interval={narrow ? 3 : 1} />
                                    <YAxis tick={{ fontSize: 11, fill: ink.muted }} width={44} tickFormatter={scheduleMeasure === 'count' ? (v => v) : formatCompact} allowDecimals={false} />
                                    <Tooltip formatter={(value, name) => [scheduleMeasure === 'count' ? `${formatNumber(value)} posts` : formatFocus(value), name]} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {schedule.networks.map(red => (
                                        <Line key={red} type="monotone" dataKey={red} stroke={NETWORK_COLORS[red] || ink.accent} strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </Section>
            <Section
                title="Mejor día y hora para publicar"
                info="mejorFranja"
                hint={heat.best
                    ? `Mejor franja: ${WEEKDAYS[heat.best.day]} ${heat.slots[heat.best.slot]} h (mediana ${formatFocus(heat.best.median)}, ${heat.best.count} posts)`
                    : `Mediana por franja; se necesitan al menos ${MIN_HEAT_POSTS} posts por celda`}
            >
                <div>
                    <table style={{ ...rt.table, tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ ...rt.th, width: 38, padding: '6px 4px' }} />
                                {heat.slots.map(s => <th key={s} style={{ ...rt.th, padding: '6px 1px', textAlign: 'center', fontSize: '0.56rem', whiteSpace: 'nowrap' }}>{s.replace(/^0/, '').replace('–0', '–')}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {heat.cells.map((day, d) => (
                                <tr key={WEEKDAYS[d]}>
                                    <td style={{ ...rt.td, padding: '4px 6px', fontWeight: 600, fontSize: '0.8rem' }}>{WEEKDAYS[d]}</td>
                                    {day.map((cell, s) => {
                                        const usable = cell.count >= MIN_HEAT_POSTS && cell.median != null;
                                        const ratio = usable && heat.maxMedian ? cell.median / heat.maxMedian : null;
                                        const isBest = heat.best && heat.best.day === d && heat.best.slot === s;
                                        return (
                                            <td
                                                key={s}
                                                title={`${WEEKDAYS[d]} ${heat.slots[s]} h · ${cell.count} posts${usable ? ` · mediana ${formatFocus(cell.median)}` : ''}`}
                                                style={{
                                                    padding: 0,
                                                    height: 34,
                                                    textAlign: 'center',
                                                    fontSize: '0.7rem',
                                                    color: ratio != null && ratio > 0.55 ? '#ffffff' : ink.muted,
                                                    backgroundColor: heatColor(ratio),
                                                    borderWidth: 2,
                                                    borderStyle: 'solid',
                                                    borderColor: isBest ? '#F9AB00' : '#ffffff',
                                                }}
                                            >
                                                {cell.count || ''}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p style={{ ...studio.hint, marginTop: 8 }}>El número es la cantidad de posts; el color, la mediana de la métrica. Borde amarillo = mejor franja.</p>
            </Section>
                    <FrequencySection frequency={frequency} formatFocus={formatFocus} metricLabel={metricLabel} narrow={narrow} onToggleExclude={onToggleExclude} />
                </>
            )}

            {subview === 'tendencias' && (
                <MomentumSection rows={momentumRows} formatFocus={formatFocus} metricLabel={metricLabel} narrow={narrow} onToggleExclude={onToggleExclude} />
            )}


            {detail && <PostDetail post={detail} metrics={metricsWithData([detail])} kpis={postKpis} onClose={() => setDetail(null)} />}
        </div>
    );
};

const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 18 },
    toolbar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
    error: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.86rem' },
    cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    cardTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 600, color: ink.text },
    grid2: { display: 'grid', gap: 18 },
    subnavScroll: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    subnav: { display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, backgroundColor: '#ece9f3' },
    subnavBtn: {
        padding: '8px 16px',
        borderRadius: 9,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        color: ink.muted,
        fontWeight: 600,
        fontSize: '0.86rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    subnavBtnOn: { backgroundColor: ink.surface, color: ink.accentText, boxShadow: '0 1px 3px rgba(28, 29, 33, 0.12)' },
    chartLabel: { margin: '0 0 6px', fontSize: '0.8rem', fontWeight: 600, color: ink.muted },
    omitBtn: {
        padding: '3px 9px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.72rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    numTd: { ...rt.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    redCell: { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: '0.86rem' },
    scoreCell: { display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' },
    scoreTrack: { position: 'relative', width: 70, height: 8, borderRadius: 4, backgroundColor: ink.wash, display: 'inline-block' },
    scoreBar: { position: 'absolute', top: 0, height: 8, borderRadius: 4 },
    tooltip: {
        padding: '8px 10px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 8,
        fontSize: '0.8rem',
        boxShadow: '0 6px 18px rgba(28, 29, 33, 0.12)',
    },
    quadrant: {
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: ink.wash,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
    },
    quadrantNames: { fontSize: '0.8rem', color: ink.text, marginTop: 4, lineHeight: 1.45 },
    topList: { display: 'flex', flexDirection: 'column', gap: 6 },
    topItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 10,
        backgroundColor: ink.surface,
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    rank: { width: 22, textAlign: 'center', fontWeight: 700, color: ink.faint, fontSize: '0.9rem' },
    topText: {
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize: '0.8rem',
        color: ink.muted,
        marginTop: 2,
    },
    topValue: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontWeight: 700, color: ink.text, fontSize: '0.92rem', whiteSpace: 'nowrap' },
    viral: { marginTop: 2, padding: '0 6px', borderRadius: 999, backgroundColor: '#fce8e6', color: ink.danger, fontSize: '0.64rem', fontWeight: 700 },
    cloud: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px 12px',
        padding: '12px 4px',
        minHeight: 160,
    },
    cloudWord: {
        background: 'none',
        border: 'none',
        padding: '2px 6px',
        borderRadius: 8,
        fontFamily: 'inherit',
        fontWeight: 600,
        lineHeight: 1.15,
        cursor: 'pointer',
    },
    wordInfo: {
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        alignItems: 'baseline',
        marginTop: 10,
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        fontSize: '0.86rem',
    },
};

export default SocialAnalysis;
